import { Spinner } from "@components/ui-kit";
import { NotFoundProject } from "@components/widgets/ErrorPage";
import { NotAllowedPage } from "@components/widgets/NotAllowedPage";
import { t } from "@lingui/macro";
import {
  BrandLinkTitle,
  ProjectTitle,
} from "@src/components/widgets/ModuleTitle";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { createRef, type RefObject, useEffect, useMemo, useState } from "react";
import {
  Category,
  CategoryNavigation,
  FooterForm,
  Save,
  Summary,
} from "./components";
import type { CategoryRefProps } from "./components/Category";
import { useFetchProject } from "./hooks";
import { Store, StoreContext } from "./store";
import type { TProject } from "./types";

interface TCategoryRefs {
  [key: string]: RefObject<CategoryRefProps>;
}

interface BudgetProps {
  /** Project.id */
  id: string;
}

const Budget = ({ id }: BudgetProps) => {
  const appStore = useStore();
  const [store] = useState(() => new Store(appStore));

  const { data, loading } = useFetchProject(id, store);

  useEffect(() => {
    store.setDefaultDate();
  }, [store.defaultDate]);

  const onSave = (project: TProject) => {
    store.setProject(project);
    appStore.UIStore.toast({ title: t`Saved`, status: "success" });
  };

  const categoryRefs = useMemo<TCategoryRefs>(() => {
    const refs: TCategoryRefs = {};

    store.ourWorkCategories.forEach((i) => {
      const ref = createRef<CategoryRefProps>();
      refs[i.key] = ref;
    });

    store.expenseCategories.forEach((i) => {
      const ref = createRef<CategoryRefProps>();
      refs[i.key] = ref;
    });

    return refs;
  }, [store.ourWorkCategories, store.expenseCategories]);

  const onInvalid = () => {
    store.selectBudgetCategory(undefined);

    /** Scroll to first invalid category */
    Object.values(categoryRefs).find((i) => i.current?.scrollToInvalid());
  };

  if (loading) {
    return <Spinner absolute />;
  }

  if (!data?.project) {
    return <NotFoundProject />;
  }

  if (!store.project) {
    return null;
  }

  if (store.project.uses_budgetovac) {
    return (
      <NotAllowedPage
        title={t`Budget page is not allowed for this project.`}
        link={{
          href: {
            pathname: "/projects/budgets",
            query: { id },
          },
          content: t`Go to BUDGETS`,
        }}
      />
    );
  }

  return (
    <StoreContext.Provider value={store}>
      <BrandLinkTitle brand={store.project.brand} />
      <ProjectTitle
        className="mb-20"
        project={store.project}
        brand={store.project.brand}
      />
      <CategoryNavigation
        ourWorkCategories={store.ourWorkCategories}
        expenseCategories={store.expenseCategories}
        selected={store.selectedBudgetCategory}
        onSelect={store.selectBudgetCategory}
      />
      <main className="space-y-16">
        {store.filteredCategories.map((i) => (
          <Category key={i.key} ref={categoryRefs[i.key]} item={i} />
        ))}
      </main>
      <footer className="space-y-16 mt-32">
        <div className="flex justify-end space-x-12">
          <FooterForm validFrom={store.valid_from} />
          <Save
            projectId={store.project.id}
            ourWork={store.ourWorkCategories}
            expenses={store.expenseCategories}
            validFrom={store.valid_from}
            onCompleted={onSave}
            onInvalid={onInvalid}
          />
        </div>
        <Summary
          showOnlyTotalBudget={!store.project.billable}
          ourWork={store.project.stats?.ourWork.total_budget ?? 0}
          plannedExpenses={store.project.stats?.expenses.planned_expenses ?? 0}
          plannedCommission={store.project.stats?.planned_commission ?? 0}
          invoiced={store.project.stats?.credit ?? 0}
          totalBudget={store.project.stats?.total_budget ?? 0}
        />
      </footer>
    </StoreContext.Provider>
  );
};

export default observer(Budget);
